import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useId,
} from "react";

const Ctx = createContext(null);

function optionText(node) {
  if (Array.isArray(node)) return node.map(optionText).join('');
  if (React.isValidElement(node)) return optionText(node.props.children);
  return node == null ? '' : String(node);
}
function optionsFrom(children) {
  return React.Children.toArray(children).flatMap(child => {
    if (!React.isValidElement(child)) return [];
    return child.type === SelectItem ? [{ value: child.props.value, label: optionText(child.props.children) }] : optionsFrom(child.props.children);
  });
}

export function Select({
  value,
  onValueChange,
  defaultValue,
  placeholder = "",
  children,
  style = {},
  className = "",
}) {
  const [internal, setInternal] = useState(defaultValue ?? "");
  const [open, setOpen] = useState(false);
  const listId = useId();
  const controlled = value !== undefined;
  const val = controlled ? value : internal;
  const label = optionsFrom(children).find(option => option.value === val)?.label || '';

  const setVal = (v, lbl) => {
    if (!controlled) setInternal(v);
    if (onValueChange) onValueChange(v, lbl);
  };

  const ref = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{ position: "relative", ...style }}
    >
      <Ctx.Provider value={{ value: val, label, setVal, open, setOpen, placeholder, listId }}>
        {children}
      </Ctx.Provider>
    </div>
  );
}

export function SelectTrigger({ children, className = "", style = {}, ...props }) {
  const ctx = useContext(Ctx);
  return (
    <button
      type="button"
      role="combobox"
      aria-expanded={ctx?.open}
      aria-controls={ctx?.listId}
      aria-haspopup="listbox"
      className={`select-trigger ${className}`}
      {...props}
      onClick={() => ctx?.setOpen?.(!ctx.open)}
      onKeyDown={e => {
        if (e.key === 'ArrowDown') { e.preventDefault(); ctx?.setOpen(true); setTimeout(() => document.getElementById(ctx?.listId)?.querySelector('[role="option"]')?.focus(),0); }
        if (e.key === 'Escape') ctx?.setOpen(false);
      }}
      style={style}
    >
      {children}
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
        <path d="m7 10 5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

export function SelectValue({ placeholder }) {
  const ctx = useContext(Ctx);
  // Show the placeholder (e.g., "All Status") until a real option is picked
  const text = ctx?.label || placeholder || ctx?.placeholder || "";
  return <span>{text}</span>;
}


export function SelectContent({ children, className = "", style = {}, ...props }) {
  const ctx = useContext(Ctx);
  if (!ctx?.open) return null;
  return (
    <div
      className={`select-content ${className}`}
      id={ctx?.listId}
      role="listbox"
      {...props}
      style={style}
    >
      {children}
    </div>
  );
}

export function SelectItem({ value, children, className = "", style = {}, ...props }) {
  const ctx = useContext(Ctx);
  return (
    <button
      type="button"
      role="option"
      aria-selected={ctx?.value === value}
      className={`select-item ${className}`}
      {...props}
      onClick={() => {
        const lbl = typeof children === "string" ? children : String(value);
        ctx?.setVal?.(value, lbl);
        ctx?.setOpen?.(false);
      }}
      onKeyDown={e => {
        const list = Array.from(e.currentTarget.parentElement.querySelectorAll('[role="option"]'));
        const index = list.indexOf(e.currentTarget);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); list[(index + (e.key === 'ArrowDown' ? 1 : list.length-1)) % list.length]?.focus(); }
        if (e.key === 'Escape') { ctx?.setOpen(false); e.currentTarget.closest('.select-content')?.parentElement.querySelector('[role="combobox"]')?.focus(); }
      }}
      style={style}
    >
      {children}
    </button>
  );
}
